import { coerceAiValue } from './coerceAiValue';

interface SuggestionTarget {
  type: string | null | undefined;
  config: Record<string, unknown>;
}

export type ResolvedSuggestion =
  | { kind: 'prose'; content: Record<string, unknown> }
  | { kind: 'value'; value: unknown }
  | { kind: 'rejected' };

/**
 * Decides what an agent suggestion may do to a section, before anything is written.
 *
 * Prose and typed sections take different write paths — one inserts into a live editor so the
 * change joins its undo history, the other sets a discrete value — and a suggestion aimed at
 * the wrong one would either be dropped or land as a TipTap document inside a date field.
 *
 * `rejected` is deliberately distinct from writing an empty value: a model that proposes an
 * option the template never offered must not get it into the brief, because the brief would
 * then record a choice nobody could have made. `coerceAiValue` keeps whatever part of the
 * suggestion IS in vocabulary and rejects only when nothing survives.
 */
export function resolveSuggestion(
  section: SuggestionTarget,
  candidate: unknown,
): ResolvedSuggestion {
  // A section with no type is prose, and so is `text` — both are the TipTap editor.
  if (!section.type || section.type === 'text') {
    return { kind: 'prose', content: candidate as Record<string, unknown> };
  }

  const value = coerceAiValue(section.type, section.config, candidate);
  return value === undefined ? { kind: 'rejected' } : { kind: 'value', value };
}
